import { ReactElement, useContext, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { HubHomePage, HubShell, preloadHubHomePage } from "../../modules/hub/route-elements";
import { CLIMATEPARTNER_INTERNAL_ACCOUNT } from "../../shared/auth/internal-account";
import { UserContext } from "../../shared/auth/user-context";
import { isEmployeeInternal } from "../../shared/auth/user-utils";
import { HOMESCREEN_PATH } from "../../shared/routing/paths/app";
import { buildCustomerPath } from "../../shared/routing/paths/customer";
import { consumeIntendedRoute, getIntendedRoute } from "./intended-route";

const INTERNAL_CUSTOMER_AREA = buildCustomerPath(CLIMATEPARTNER_INTERNAL_ACCOUNT);

/**
 * The one deep link staff must not be handed: `/customer/{id}/**` renders against the account, and
 * `climatepartner-internal` is not a customer. Reachable only by hand or from a stale bookmark.
 */
const isInternalCustomerArea = (path: string): boolean => {
  const pathname = path.split("?")[0].replace(/\/+$/, "");

  return pathname === INTERNAL_CUSTOMER_AREA || pathname.startsWith(`${INTERNAL_CUSTOMER_AREA}/`);
};

export const HomeEntryRoute = (): ReactElement => {
  const { hasPermission } = useContext(UserContext);
  const location = useLocation();

  const intendedRoute = getIntendedRoute();

  // `AppLayout` spends it only for a route that renders; clearing it here too means a destination
  // that bounces straight back to `/` (a guard denial) cannot ping-pong. Above the staff branch on
  // purpose: an intent that branch drops would otherwise fire on the next walk back to `/`.
  useEffect(() => {
    if (intendedRoute !== null) {
      consumeIntendedRoute();
    }
  }, [intendedRoute]);

  const isStaff = isEmployeeInternal(hasPermission);

  // Staff have no customer of their own, so without a deep link they pick one on the hub. A deep link
  // names the account, so it wins, except one into the internal account's own customer area.
  const isStaffWithoutUsableDeepLink = isStaff && (intendedRoute === null || isInternalCustomerArea(intendedRoute));

  const willRenderHub =
    location.pathname === HOMESCREEN_PATH && (isStaffWithoutUsableDeepLink || intendedRoute === null);

  useEffect(() => {
    if (willRenderHub) {
      preloadHubHomePage(isStaff);
    }
  }, [willRenderHub, isStaff]);

  // Rendered in place so the address bar stays on `/`. `/oauth/callback` is sent to `/` first rather
  // than showing the hub under an OAuth URL (which would also park the `code` in history).
  const renderHub = (): ReactElement =>
    location.pathname === HOMESCREEN_PATH ? (
      <HubShell>
        <HubHomePage />
      </HubShell>
    ) : (
      <Navigate to={HOMESCREEN_PATH} replace />
    );

  if (isStaffWithoutUsableDeepLink) {
    return renderHub();
  }

  if (intendedRoute !== null) {
    return <Navigate to={intendedRoute} replace />;
  }

  return renderHub();
};
